import http from "../base/http.service";
import { AxiosRequestConfig } from "axios";

export interface DynamicChoiceDto {
  id?: number;
  key?: string;
  text: string;
  is_correct?: boolean;
}

export interface DynamicQuestionDto {
  id?: number;
  key?: string;
  text: string;
  question_type: "text" | "single_choice" | "dropdown" | "multiple_choice" | "rating" | "date" | "number";
  order: number;
  section_key?: string;
  help_text?: string;
  is_required?: boolean;
  config?: { min?: number | null; max?: number | null };
  choices?: DynamicChoiceDto[];
}

export interface DynamicRuleConditionDto {
  question_key: string;
  operator: "equals" | "not_equals" | "contains" | "contains_all" | "contains_any" | "greater_than" | "greater_or_equal" | "less_than" | "less_or_equal" | "answered" | "not_answered";
  value?: string | number | Array<string | number>;
}

export interface DynamicRuleDto {
  id?: number;
  source_section_key?: string | null;
  priority: number;
  match_all: boolean;
  conditions: DynamicRuleConditionDto[];
  action_type: "hide_question" | "goto_section" | "submit";
  target_question_key?: string | null;
  target_section_key?: string | null;
}

export interface DynamicSectionDto {
  key: string;
  title: string;
  description?: string;
  order: number;
}

export interface DynamicSurveyDto {
  id?: number;
  title: string;
  description?: string;
  is_active: boolean;
  is_deleted?: boolean;
  created_at?: string;
  questions?: DynamicQuestionDto[];
  sections?: DynamicSectionDto[];
  rules?: DynamicRuleDto[];
  has_submitted?: boolean;
  share_token?: string;
  access_mode?: "public" | "authenticated";
  starts_at?: string | null;
  ends_at?: string | null;
  response_limit?: number | null;
  is_anonymous?: boolean;
  response_count?: number;
  is_closed?: boolean;
  closure_reason?: "inactive" | "not_started" | "expired" | "limit_reached" | null;
}

export interface DynamicAnswerSubmitDto {
  question_id: number;
  text_answer?: string;
  selected_choices?: number[];
  rating_answer?: number;
  number_answer?: number | null;
}

export interface DynamicResponseSubmitDto {
  answers: DynamicAnswerSubmitDto[];
}

export interface SurveyAnalysisQuestion {
  id: number;
  text: string;
  question_type: "text" | "single_choice" | "dropdown" | "multiple_choice" | "rating" | "date" | "number";
  order: number;
  section_title?: string;
  respondent_count?: number;
  results: any; // dynamic based on question_type
}

export interface SurveyAnalysisDto {
  id?: number;
  survey_title: string;
  survey_description?: string;
  is_active?: boolean;
  access_mode?: "public" | "authenticated";
  response_limit?: number | null;
  is_anonymous?: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  share_token?: string | null;
  sections_count?: number;
  total_responses: number;
  questions: SurveyAnalysisQuestion[];
}

const dynamicSurveyService = {
  getSurveys(config?: AxiosRequestConfig) {
    return http.get<DynamicSurveyDto[]>("forms/api/surveys", config);
  },

  getSurveyDetail(id: number | string, config?: AxiosRequestConfig) {
    return http.get<DynamicSurveyDto>(`forms/api/surveys/${id}`, { overrideError: true, ...config });
  },

  getPublicSurvey(token: string, config?: AxiosRequestConfig) {
    return http.get<DynamicSurveyDto>(`forms/api/public/surveys/${token}`, { overrideError: true, ...config });
  },

  createSurvey(dto: DynamicSurveyDto, config?: AxiosRequestConfig) {
    return http.post<DynamicSurveyDto>("forms/api/surveys", dto, { overrideError: true, ...config });
  },

  updateSurvey(id: number | string, dto: Partial<DynamicSurveyDto>, config?: AxiosRequestConfig) {
    return http.put<DynamicSurveyDto>(`forms/api/surveys/${id}`, dto, { overrideError: true, ...config });
  },

  deleteSurvey(id: number | string, config?: AxiosRequestConfig) {
    return http.delete<string>(`forms/api/surveys/${id}`, config);
  },

  submitSurveyResponse(surveyId: number | string, dto: DynamicResponseSubmitDto, config?: AxiosRequestConfig) {
    return http.post<string>(`forms/api/surveys/${surveyId}/submit`, dto, { overrideError: true, ...config });
  },

  submitPublicSurveyResponse(token: string, dto: DynamicResponseSubmitDto, config?: AxiosRequestConfig) {
    return http.post<string>(`forms/api/public/surveys/${token}`, dto, { overrideError: true, ...config });
  },

  checkSurveySubmission(surveyId: number | string, config?: AxiosRequestConfig) {
    return http.get<{ has_submitted: boolean }>(`forms/api/surveys/${surveyId}/check-submission`, config);
  },

  getSurveyAnalysis(surveyId: number | string, config?: AxiosRequestConfig) {
    return http.get<SurveyAnalysisDto>(`forms/api/surveys/${surveyId}/analysis`, config);
  }
};

export default dynamicSurveyService;
